// features/countries/page/countries-list/countries-list.ts
import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit,
  ViewChildren,
  QueryList,
} from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { MatTabsModule } from '@angular/material/tabs';
import { Sort } from '@angular/material/sort';

import { MatDialog, MatDialogModule } from '@angular/material/dialog';

import { UiMoleculeMatTableComponent } from '@shared/components/molecules/ui-molecule-mat-table/ui-molecule-mat-table.component';
import { TableColumn } from '@shared/components/molecules/ui-molecule-mat-table/interfaces/table-column';
import { TableAction } from '@shared/components/molecules/ui-molecule-mat-table/interfaces/table-action';
import { ActionEvent } from '@shared/components/molecules/ui-molecule-mat-table/interfaces/action-event';

import { UiAtomButtonComponent } from '@shared/components/atoms/ui-atom-button/ui-atom-button.component';
import { UiAtomGlobalSearchComponent } from '@shared/components/atoms/ui-atom-global-search/ui-atom-global-search.component';
import { UiMoleculeDialogDeleteWithConfirmComponent } from '@shared/components/molecules/ui-molecule-dialog-delete-with-confirm/ui-molecule-dialog-delete-with-confirm.component';
import { UiOrganismMainNavComponent } from '@shared/components/organisms/ui-organism-main-nav/ui-organism-main-nav.component';
import { UiAtomSpacerComponent } from '@shared/components/atoms/ui-atom-spacer/ui-atom-spacer.component';
import { UiAtomTextComponent } from '@shared/components/atoms/ui-atom-text/ui-atom-text.component';
import {
  CountryUpsertDialogComponent,
  CountryDialogResult,
} from '@shared/components/molecules/ui-molecule-dialog-country-upsert/ui-molecule-dialog-country-upsert.component';
import {
  FormUpsertDialogComponent,
  FormDialogResult,
} from '@shared/components/molecules/ui-molecule-dialog-form-upsert/ui-molecule-dialog-form-upsert.component';
import {
  FormResponseDialogComponent,
  FormResponseData,
} from '@shared/components/molecules/ui-molecule-dialog-form-response/ui-molecule-dialog-form-response.component';
import {
  DialogAdminUpsertService,
  AdminDialogResult,
} from '@shared/services/dialog-admin-upsert.service';
import {
  UserService,
  UserCreate,
  UserUpdate,
  UserListParams,
} from '@core/services/use-cases/user.service';
import { UiMoleculeDialogAdminUpsertComponent } from '@shared/components/molecules/ui-molecule-dialog-admin-upsert/ui-molecule-dialog-admin-upsert.component';

import { TranslatePipe, TranslateService } from '@ngx-translate/core';

import { Country } from '@core/entities/models/country.model';
import { CountryService, CountryListParams } from '@core/services/use-cases/country.service';
import { FormListParams } from '@core/services/use-cases/form.service';
import { PartnerService } from '@core/services/use-cases/partner.service';
import { FormService } from '@core/services/use-cases/form.service';
import { DialogFormDashboardService } from '@shared/services/dialog-form-dashboard.service';
import { RoleRedirectService } from '@core/services/role-redirect.service';
import { AppRoutes } from '@core/constants/app.routes';
import { SnackbarService } from '@shared/services/snackbar.service';
import { SessionService } from '@core/services/use-cases/session.service';
import { UserRole } from '@core/entities/models/role.model';
import { formatDate } from '@core/utils/date.utils';
import {
  UiOrganismTitleLogosComponent
} from '@shared/components/organisms/ui-organism-title-logos/ui-organism-title-logos.component';

interface CountryListRow {
  id: string;
  name: string;
  createdAt: string;
  updatedAt: string;
  activation: string;
  partnersCount: number;
  beneficiariesCount: number;
}

@Component({
  selector: 'app-countries-list',
  standalone: true,
  imports: [
    CommonModule,
    MatTabsModule,
    UiMoleculeMatTableComponent,
    UiAtomButtonComponent,
    MatDialogModule,
    TranslatePipe,
    UiOrganismMainNavComponent,
    UiAtomSpacerComponent,
    UiAtomTextComponent,
    UiMoleculeDialogAdminUpsertComponent,
    FormUpsertDialogComponent,
    CountryUpsertDialogComponent,
    UiAtomGlobalSearchComponent,
    UiOrganismTitleLogosComponent,
  ],
  templateUrl: './countries-list.html',
  styleUrl: './countries-list.scss',
  changeDetection: ChangeDetectionStrategy.Default,
})
export class CountriesList implements OnInit {
  @ViewChildren(UiMoleculeMatTableComponent) tables!: QueryList<UiMoleculeMatTableComponent>;

  protected tableData: CountryListRow[] = [];
  protected tableColumns: TableColumn[] = [];
  protected usersData: any[] = []; // Datos de usuarios admin_countries
  protected userColumns: TableColumn[] = []; // Columnas para tabla de usuarios
  protected formsData: any[] = []; // Datos de forms globales
  protected formsColumns: TableColumn[] = []; // Columnas para tabla de forms

  // Título dinámico según nivel jerárquico
  protected pageTitle = 'SMART UA'; // Nivel 1: Super Admin Dashboard

  protected readonly tableActions: TableAction[] = [
    {
      icon: 'edit',
      tooltip: 'Edit country',
      tooltipKey: 'countries.list.table.actions.edit',
      event: 'edit',
    },
    {
      icon: 'delete',
      tooltip: 'Delete country',
      tooltipKey: 'countries.list.table.actions.delete',
      event: 'delete',
    },
  ];

  protected readonly userActions: TableAction[] = [
    {
      icon: 'person',
      tooltip: 'Deactivate user',
      tooltipKey: 'users.list.table.actions.deactivate',
      event: 'toggle-user-status',
      showIf: (user: any) => user.status === 'ACTIVE',
    },
    {
      icon: 'person_off',
      tooltip: 'Activate user',
      tooltipKey: 'users.list.table.actions.activate',
      event: 'toggle-user-status',
      showIf: (user: any) => user.status === 'BLOCKED',
    },
    {
      icon: 'edit',
      tooltip: 'Edit user',
      tooltipKey: 'users.list.table.actions.edit',
      event: 'edit-user',
    },
    {
      icon: 'delete',
      tooltip: 'Delete user',
      tooltipKey: 'users.list.table.actions.delete',
      event: 'delete-user',
    },
  ];

  protected readonly formsActions: TableAction[] = [
    {
      icon: 'rocket',
      tooltip: 'Publish',
      tooltipKey: 'forms.list.table.actions.publish',
      event: 'publish-form',
      showIf: (form: any) => {
        // Mostrar cohete si publication es null (no publicado en este nivel)
        return form.publication === null && this.canPublishToCountry();
      },
    },
    {
      icon: 'edit',
      tooltip: 'Edit form',
      tooltipKey: 'forms.list.table.actions.edit',
      event: 'edit-form',
      showIf: (form: any) => form.status === 'DRAFT', // Solo mostrar si está en borrador
    },
    {
      icon: 'delete',
      tooltip: 'Delete form',
      tooltipKey: 'forms.list.table.actions.delete',
      event: 'delete-form',
      showIf: (form: any) => form.status === 'DRAFT', // Solo mostrar si está en borrador
    },
    {
      icon: 'space_dashboard',
      tooltip: 'View dashboard',
      tooltipKey: 'forms.list.table.actions.dashboard',
      event: 'dashboard-form',
      // Tab Forms global: solo SUPER_ADMIN, sobre forms publicados.
      showIf: (form: any) =>
        form.status === 'FINAL' &&
        !!form.publication &&
        this.sessionService.getCurrentUser()?.role === UserRole.ADMIN,
      disabledIf: (form: any) => {
        // El endpoint global no devuelve `submissions.total`. Cuando llegue se
        // aprovechará automáticamente; de momento solo deshabilita si no hay
        // publicación (caso ya filtrado por showIf).
        if (!form.publication) return true;
        const total = form.publication.submissions?.total;
        return total !== undefined && total === 0;
      },
    },
  ];

  protected shouldShowFinalizeButton(form: any): boolean {
    return form.status === 'DRAFT';
  }

  protected pageIndex = 0;
  protected pageSize = 10;
  protected totalItems = 0;
  protected userPageIndex = 0;
  protected userPageSize = 10;
  protected userTotalItems = 0;
  protected formsPageIndex = 0;
  protected formsPageSize = 10;
  protected formsTotalItems = 0;
  protected selectedTab = 0;
  protected isLoading = false;
  protected isLoadingUsers = false;
  protected isLoadingForms = false;

  // Ordenación servidor - Countries
  protected countrySortBy: CountryListParams['sortBy'] = undefined;
  protected countrySortOrder: 'ASC' | 'DESC' | undefined = undefined;
  protected countryFilter: string | undefined = undefined;

  // Ordenación servidor - Users
  protected userSortBy: UserListParams['sortBy'] = undefined;
  protected userSortOrder: 'ASC' | 'DESC' | undefined = undefined;
  protected userFilter: string | undefined = undefined;

  // Ordenación servidor - Forms
  protected formsSortBy: FormListParams['sortBy'] = undefined;
  protected formsSortOrder: 'ASC' | 'DESC' | undefined = undefined;
  protected formsFilter: string | undefined = undefined;

  constructor(
    private readonly countryService: CountryService,
    private readonly partnerService: PartnerService,
    private readonly formService: FormService,
    private readonly userService: UserService,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly dialog: MatDialog,
    private readonly cdr: ChangeDetectorRef,
    private readonly translateService: TranslateService,
    private readonly roleRedirectService: RoleRedirectService,
    private readonly dialogAdminUpsert: DialogAdminUpsertService,
    private readonly snackbarService: SnackbarService,
    private readonly sessionService: SessionService,
    private readonly dashboardDialog: DialogFormDashboardService,
  ) {}

  /**
   * Verifica si el usuario actual puede publicar a nivel country (SUPERADMIN o COUNTRY_ADMIN)
   */
  private canPublishToCountry(): boolean {
    const currentUser = this.sessionService.getCurrentUser();
    return currentUser?.role === UserRole.ADMIN || currentUser?.role === UserRole.COUNTRY_ADMIN;
  }

  ngOnInit(): void {
    this.buildTableColumns();
    this.buildUserColumns();
    this.buildFormsColumns();
    // Solo cargar la primera tab inicialmente
    this.loadTable();
  }

  private buildTableColumns(): void {
    this.tableColumns = [
      {
        key: 'name',
        title: 'Name',
        titleKey: 'countries.list.table.columns.name',
        type: 'text',
        sortable: true,
        filterable: true,
        filterType: 'input',
        placeholder: 'Filter by country name',
        placeholderKey: 'countries.list.table.filters.name',
      },
      // HIDDEN-COLUMNS: 'createdAt' y 'updatedAt' ocultas temporalmente en la pestaña Countries.
      // Para volver a mostrarlas, descomentar el bloque siguiente (no requiere más cambios:
      // las claves i18n y el mapeo de datos siguen vigentes).
      // {
      //   key: 'createdAt',
      //   title: 'CreatedAt',
      //   titleKey: 'countries.list.table.columns.createdAt',
      //   type: 'text',
      //   width: '120px',
      //   sortable: true,
      //   filterable: false,
      // },
      // {
      //   key: 'updatedAt',
      //   title: 'UpdatedAt',
      //   titleKey: 'countries.list.table.columns.updatedAt',
      //   type: 'text',
      //   width: '120px',
      //   sortable: true,
      //   filterable: false,
      // },
      {
        key: 'partnersCount',
        title: 'Partners',
        titleKey: 'countries.list.table.columns.partners',
        type: 'text',
        width: '100px',
        sortable: true,
        filterable: false,
      },
    ];
  }

  private loadTable(searchTerm?: string): void {
    this.isLoading = true;
    const params: CountryListParams = {
      offset: this.pageIndex * this.pageSize,
      limit: this.pageSize,
    };
    if (searchTerm && searchTerm.length >= 3) {
      params.filter = searchTerm;
    }
    if (this.countrySortBy) {
      params.sortBy = this.countrySortBy;
    }
    if (this.countrySortOrder) {
      params.sortOrder = this.countrySortOrder;
    }

    this.countryService.getAll(params).subscribe({
      next: (response) => {
        // Usar los contadores reales de la API
        this.tableData = response.items.map((country) => ({
          id: country.id,
          name: country.name,
          createdAt: formatDate(country.createdAt),
          updatedAt: formatDate(country.updatedAt),
          activation: country.activation,
          partnersCount: country.counts?.partners || 0,
          beneficiariesCount: 0, // TODO: Obtener de country.counts.beneficiaries cuando esté disponible
        }));
        this.totalItems = response.total;
        this.isLoading = false;
        this.cdr.markForCheck();
      },
      error: (err) => {
        this.snackbarService.notifyHttpError(err);
        this.isLoading = false;
      },
    });
  }

  protected onTableAction(actionEvent: ActionEvent): void {
    const { event, rowData } = actionEvent;
    const row = rowData as Country;

    switch (event) {
      case 'edit':
        this.onEdit(row);
        break;
      case 'delete':
        this.onDelete(row);
        break;
      default:
        break;
    }
  }

  protected onTableRowClick(row: Country): void {
    // Navegación jerárquica: al hacer click en un país, ir a sus partners
    const navigationUrl = this.roleRedirectService.buildNavigationUrl('country', row.id);
    void this.router.navigate(navigationUrl).catch(() => {});
  }

  protected onTablePageChange(event: { pageIndex: number; pageSize: number }): void {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.loadTable();
  }

  protected onTableSortChange(sort: Sort): void {
    // Mapear columna de tabla a campo de API
    const columnToApiField: Record<string, CountryListParams['sortBy']> = {
      name: 'name',
      createdAt: 'createdAt',
      updatedAt: 'updatedAt',
      partnersCount: 'counts.partners',
    };

    if (sort.direction) {
      this.countrySortBy = columnToApiField[sort.active];
      this.countrySortOrder = sort.direction.toUpperCase() as 'ASC' | 'DESC';
    } else {
      this.countrySortBy = undefined;
      this.countrySortOrder = undefined;
    }

    this.pageIndex = 0; // Reset a primera página
    this.loadTable();
  }

  private onEdit(row: Country): void {
    this.openEditCountryDialog(row);
  }

  private openEditCountryDialog(country: Country): void {
    const dialogRef = this.dialog.open(CountryUpsertDialogComponent, {
      width: '600px',
      maxHeight: '90vh',
      panelClass: ['dialog-md', 'dialog-safe-area'],
      data: {
        title: this.translateService.instant('countries.form.editTitle'),
        mode: 'edit',
        country: country,
      },
    });

    dialogRef.afterClosed().subscribe((result: CountryDialogResult | undefined) => {
      if (result && result.mode === 'edit' && result.countryId) {
        this.handleCountryUpdate(result.countryId, result.payload);
      }
    });
  }

  private handleCountryUpdate(countryId: string, payload: any): void {
    this.countryService.update(countryId, payload).subscribe({
      next: () => {
        this.loadTable();
      },
      error: (err) => this.snackbarService.notifyHttpError(err),
    });
  }

  private onDelete(row: Country): void {
    const dialogRef = this.dialog.open(UiMoleculeDialogDeleteWithConfirmComponent, {
      disableClose: true,
      autoFocus: false,
      panelClass: ['dialog-sm', 'dialog-safe-area'],
      data: { itemName: row.name },
    });

    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (!confirmed) return;
      this.countryService.delete(row.id).subscribe({
        next: () => this.loadTable(),
        error: (err) => this.snackbarService.notifyHttpError(err),
      });
    });
  }

  protected goToCreate(): void {
    this.openCreateCountryDialog();
  }

  private openCreateCountryDialog(): void {
    const dialogRef = this.dialog.open(CountryUpsertDialogComponent, {
      width: '600px',
      maxHeight: '90vh',
      panelClass: ['dialog-md', 'dialog-safe-area'],
      data: {
        title: this.translateService.instant('countries.form.createTitle'),
        mode: 'create',
      },
    });

    dialogRef.afterClosed().subscribe((result: CountryDialogResult | undefined) => {
      if (result && result.mode === 'create') {
        this.handleCountryCreate(result.payload);
      }
    });
  }

  private handleCountryCreate(payload: any): void {
    this.countryService.create(payload).subscribe({
      next: () => {
        this.snackbarService.open(
          this.translateService.instant('countries.messages.created')
        );
        this.loadTable();
      },
      error: (err) => this.snackbarService.notifyHttpError(err),
    });
  }

  private buildUserColumns(): void {
    this.userColumns = [
      {
        key: 'email',
        title: 'Email',
        titleKey: 'users.list.table.columns.email',
        type: 'email',
        width: '300px',
        sortable: true,
        filterable: false,
      },
      {
        key: 'firstName',
        title: 'FirstName',
        titleKey: 'users.list.table.columns.firstName',
        type: 'text',
        sortable: true,
        filterable: false,
      },
      {
        key: 'lastName',
        title: 'LastName',
        titleKey: 'users.list.table.columns.lastName',
        type: 'text',
        sortable: true,
        filterable: false,
      },
      {
        key: 'enabled',
        title: 'Enabled',
        titleKey: 'users.list.table.columns.enabled',
        type: 'text',
        width: '90px',
        filterable: false,
      },
      // HIDDEN-COLUMNS: 'createdAt' y 'updatedAt' ocultas temporalmente en la pestaña Users.
      // Para volver a mostrarlas, descomentar el bloque siguiente (no requiere más cambios:
      // las claves i18n y el mapeo de datos siguen vigentes).
      // {
      //   key: 'createdAt',
      //   title: 'CreatedAt',
      //   titleKey: 'users.list.table.columns.createdAt',
      //   type: 'text',
      //   width: '120px',
      //   sortable: true,
      //   filterable: false,
      // },
      // {
      //   key: 'updatedAt',
      //   title: 'UpdatedAt',
      //   titleKey: 'users.list.table.columns.updatedAt',
      //   type: 'text',
      //   width: '120px',
      //   sortable: true,
      //   filterable: false,
      // },
    ];
  }

  private loadUsers(): void {
    this.isLoadingUsers = true;
    const params: UserListParams = {
      offset: this.userPageIndex * this.userPageSize,
      limit: this.userPageSize,
      role: 'SUPER_ADMIN',
    };

    if (this.userFilter && this.userFilter.length >= 3) {
      params.filter = this.userFilter;
    }
    if (this.userSortBy) {
      params.sortBy = this.userSortBy;
    }
    if (this.userSortOrder) {
      params.sortOrder = this.userSortOrder;
    }

    this.userService.getAll(params).subscribe({
      next: (users) => {
        // Mapear usuarios al formato esperado por la tabla
        this.usersData = users.items.map((user) => ({
          id: user.id,
          email: user.email,
          firstName: user.firstName,
          lastName: user.lastName,
          enabled: user.status === 'ACTIVE' ? '✓' : '',
          status: user.status, // ✅ CONSERVAR la propiedad original
          createdAt: formatDate(user.createdAt),
          updatedAt: formatDate(user.updatedAt),
        }));
        this.userTotalItems = users.total;
        this.isLoadingUsers = false;
        this.cdr.markForCheck();
      },
      error: (error) => {
        this.snackbarService.notifyHttpError(error);
        this.usersData = [];
        this.isLoadingUsers = false;
        this.cdr.markForCheck();
      },
    });
  }

  protected openCreateUserDialog(): void {
    // Abrir diálogo para crear Super Admin (nivel global) con rol por defecto
    this.dialogAdminUpsert
      .open({
        title: 'Create Super Admin',
        message: 'Create a new global administrator with access to all countries and partners.',
        defaults: {
          role: 'SUPER_ADMIN', // Rol por defecto, no se puede cambiar
        },
        availableRoles: ['SUPER_ADMIN'], // Solo este rol disponible
        context: 'global',
        mode: 'create',
      })
      .subscribe((result: AdminDialogResult | undefined) => {
        if (result && result.mode === 'create') {
          this.userService.create(result.payload as UserCreate).subscribe({
            next: () => {
              // Recargar la lista de usuarios
              this.loadUsers();
            },
            error: (error) => this.snackbarService.notifyHttpError(error),
          });
        }
      });
  }

  protected onUserAction(actionEvent: ActionEvent): void {
    const { event, rowData } = actionEvent;

    switch (event) {
      case 'edit-user':
        this.openEditUserDialog(rowData);
        break;
      case 'toggle-user-status':
        this.toggleUserStatus(rowData);
        break;
      case 'delete-user':
        this.openDeleteUserDialog(rowData);
        break;
      default:
        break;
    }
  }

  private openEditUserDialog(user: any): void {
    this.dialogAdminUpsert
      .open({
        title: 'Edit Super Admin',
        message: 'Edit the global administrator details.',
        defaults: {
          firstName: user.firstName || user.name?.split(' ')[0] || '',
          lastName: user.lastName || user.name?.split(' ').slice(1).join(' ') || '',
          email: user.email,
          role: user.role,
        },
        availableRoles: ['SUPER_ADMIN'],
        context: 'global',
        mode: 'edit',
        user: user,
      })
      .subscribe((result: AdminDialogResult | undefined) => {
        if (result && result.mode === 'edit' && result.userId) {
          this.handleUserUpdate(result.userId, result.payload as UserUpdate, result.newPassword);
        }
      });
  }

  private openDeleteUserDialog(user: any): void {
    const dialogRef = this.dialog.open(UiMoleculeDialogDeleteWithConfirmComponent, {
      disableClose: true,
      autoFocus: false,
      panelClass: ['dialog-sm', 'dialog-safe-area'],
      data: { itemName: user.name },
    });

    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (!confirmed) return;
      this.userService.delete(user.id).subscribe({
        next: () => this.loadUsers(),
        error: (err) => this.snackbarService.notifyHttpError(err),
      });
    });
  }

  protected onUserPageChange(event: { pageIndex: number; pageSize: number }): void {
    this.userPageIndex = event.pageIndex;
    this.userPageSize = event.pageSize;
    this.loadUsers();
  }

  protected onUserSortChange(sort: Sort): void {
    const columnToApiField: Record<string, UserListParams['sortBy']> = {
      email: 'email',
      firstName: 'firstName',
      lastName: 'lastName',
      createdAt: 'createdAt',
      updatedAt: 'updatedAt',
    };

    if (sort.direction) {
      this.userSortBy = columnToApiField[sort.active];
      this.userSortOrder = sort.direction.toUpperCase() as 'ASC' | 'DESC';
    } else {
      this.userSortBy = undefined;
      this.userSortOrder = undefined;
    }

    this.userPageIndex = 0;
    this.loadUsers();
  }

  protected onUserGlobalSearch(searchTerm: string): void {
    this.userFilter = searchTerm;
    if (searchTerm && searchTerm.length >= 3) {
      this.userPageIndex = 0;
      this.loadUsers();
    } else if (!searchTerm || searchTerm.length === 0) {
      this.userFilter = undefined;
      this.loadUsers();
    }
  }

  protected onTabChange(index: number): void {
    this.selectedTab = index;

    // Cargar datos según la tab seleccionada (lazy loading)
    switch (index) {
      case 0: // Countries tab
        if (this.tableData.length === 0) {
          this.loadTable();
        }
        break;
      case 1: // Forms tab
        if (this.formsData.length === 0) {
          this.loadForms();
        }
        break;
      case 2: // Users tab
        if (this.usersData.length === 0) {
          this.loadUsers();
        }
        break;
    }
  }

  protected onGlobalSearch(searchTerm: string): void {
    if (searchTerm && searchTerm.length >= 3) {
      this.pageIndex = 0;
      this.loadTable(searchTerm);
    } else if (!searchTerm || searchTerm.length === 0) {
      this.loadTable();
    }
  }

  protected onTableFilterChange(filters: Record<string, string>): void {
    const filterValue = Object.values(filters).find((v) => v && v.length >= 3);
    if (filterValue) {
      this.pageIndex = 0;
      this.loadTable(filterValue);
    } else if (Object.values(filters).every((v) => !v || v.length === 0)) {
      this.loadTable();
    }
  }

  // Forms tab methods
  private buildFormsColumns(): void {
    this.formsColumns = [
      {
        key: 'name',
        title: 'Name',
        titleKey: 'forms.list.table.columns.name',
        type: 'text',
        sortable: true,
        filterable: true,
        filterType: 'input',
        placeholder: 'Filter by name',
        placeholderKey: 'forms.list.table.filters.name',
      },
      {
        key: 'published',
        title: 'Published',
        titleKey: 'forms.list.table.columns.published',
        type: 'text',
        width: '90px',
        filterable: false,
      },
      // HIDDEN-COLUMNS: 'createdAt' y 'updatedAt' ocultas temporalmente en la pestaña Forms.
      // Para volver a mostrarlas, descomentar el bloque siguiente (no requiere más cambios:
      // las claves i18n y el mapeo de datos siguen vigentes).
      // {
      //   key: 'createdAt',
      //   title: 'Created at',
      //   titleKey: 'forms.list.table.columns.createdAt',
      //   type: 'text',
      //   width: '120px',
      //   sortable: true,
      //   filterable: false,
      // },
      // {
      //   key: 'updatedAt',
      //   title: 'Updated at',
      //   titleKey: 'forms.list.table.columns.updatedAt',
      //   type: 'text',
      //   width: '120px',
      //   sortable: true,
      //   filterable: false,
      // },
      {
        key: 'countries',
        title: 'Countries',
        titleKey: 'forms.list.table.columns.countries',
        type: 'text',
        width: '100px',
        sortable: true,
        filterable: false,
      },
    ];
  }

  private loadForms(): void {
    this.isLoadingForms = true;
    const params: FormListParams = {
      offset: this.formsPageIndex * this.formsPageSize,
      limit: this.formsPageSize,
    };

    if (this.formsFilter && this.formsFilter.length >= 3) {
      params.filter = this.formsFilter;
    }
    if (this.formsSortBy) {
      params.sortBy = this.formsSortBy;
    }
    if (this.formsSortOrder) {
      params.sortOrder = this.formsSortOrder;
    }

    this.formService.getAll(params).subscribe({
      next: (response) => {
        // Mapear los datos de Form al formato esperado por la tabla
        this.formsData = response.items.map((form) => {
          const mappedForm = {
            id: form.id,
            name: form.name,
            status: form.status,
            publication: form.publication, // Incluir publication para showIf del cohete
            countryId: (form as any).countryId, // Incluir countryId si existe
            published: form.status === 'FINAL' ? '✓' : '',
            createdAt: formatDate(form.createdAt),
            updatedAt: formatDate(form.updatedAt),
            countries: form.publication?.assignments ?? 0,
          };
          return mappedForm;
        });
        this.formsTotalItems = response.total;
        this.isLoadingForms = false;
        this.cdr.markForCheck();
      },
      error: (error) => {
        this.snackbarService.notifyHttpError(error);
        this.isLoadingForms = false;
        this.cdr.markForCheck();
      },
    });
  }

  protected openCreateFormDialog(): void {
    this.openCreateFormDialogInContext();
  }

  private openCreateFormDialogInContext(): void {
    const dialogRef = this.dialog.open(FormUpsertDialogComponent, {
      width: '90vw',
      maxWidth: '1400px',
      height: '90vh',
      panelClass: ['dialog-fullscreen', 'dialog-safe-area', 'dialog-flex'],
      data: {
        title: this.translateService.instant('forms.form.createTitle'),
        mode: 'create',
        context: 'country',
        countryId: this.getCurrentCountryId(),
      },
    });

    dialogRef.afterClosed().subscribe((result: FormDialogResult | undefined) => {
      if (result && result.mode === 'create') {
        this.handleFormCreate(result.payload, result.context, result.countryId);
      }
    });
  }

  private handleFormCreate(payload: any, context?: string, countryId?: string): void {
    // Por ahora, solo crear form global
    this.formService.create(payload).subscribe({
      next: () => {
        this.loadForms();
      },
      error: (error) => this.snackbarService.notifyHttpError(error),
    });
  }

  private handleFormUpdate(formId: string, payload: any): void {
    this.formService.update(formId, payload).subscribe({
      next: () => {
        this.loadForms();
      },
      error: (error) => this.snackbarService.notifyHttpError(error),
    });
  }

  private getCurrentCountryId(): string | undefined {
    // Obtener el countryId del contexto actual
    // Esto depende de cómo estés navegando a la lista de countries
    // Por ahora, vamos a asumir que estamos en contexto global
    return undefined;
  }

  protected onFormAction(actionEvent: ActionEvent): void {
    const { event, rowData } = actionEvent;

    switch (event) {
      case 'edit-form':
        this.editForm(rowData);
        break;
      case 'publish-form':
        this.publishFormToCountry(rowData);
        break;
      case 'delete-form':
        this.deleteForm(rowData);
        break;
      case 'toggle-assign':
        this.toggleFormAssignment(rowData);
        break;
      case 'dashboard-form':
        this.openDashboardForm(rowData);
        break;
      default:
        break;
    }
  }

  private openDashboardForm(form: any): void {
    this.formService.getById(form.id).subscribe({
      next: (fullForm) => {
        this.formService.getAllSubmittedAnswersForForm(form.id).subscribe({
          next: (responses) => {
            this.dashboardDialog.open({
              formName: fullForm.name,
              schema: fullForm.schema ?? {},
              responses,
              scopeLabel: this.translateService.instant('forms.dashboard.scope.global'),
            });
          },
          error: () => {
            this.snackbarService.open(
              this.translateService.instant('forms.dashboard.empty')
            );
          },
        });
      },
      error: (err) => this.snackbarService.notifyHttpError(err),
    });
  }

  /**
   * Abre el formulario en modo lectura al hacer click en una fila
   */
  protected onFormRowClick(form: any): void {
    this.formService.getById(form.id).subscribe({
      next: (fullForm) => {
        const dialogData: FormResponseData = {
          title: this.translateService.instant('forms.form.viewTitle'),
          formName: fullForm.name,
          schema: fullForm.schema || {},
          mode: 'view',
          formId: fullForm.id,
        };

        this.dialog.open(FormResponseDialogComponent, {
          data: dialogData,
          panelClass: ['dialog-xl', 'dialog-safe-area', 'dialog-flex'],
          width: '90vw',
          maxWidth: '1200px',
          height: '90vh',
          disableClose: false,
        });
      },
      error: (err) => this.snackbarService.notifyHttpError(err),
    });
  }

  private publishFormToCountry(form: any): void {
    const countryId = form.countryId || this.getCurrentCountryId();

    if (countryId) {
      // Si tenemos countryId, publicar a ese country específico
      this.formService.publishToCountry(countryId, form.id).subscribe({
        next: () => {
          this.snackbarService.open(
            this.translateService.instant('forms.list.messages.publishedToCountry')
          );
          this.loadForms();
        },
        error: (error) => this.snackbarService.notifyHttpError(error),
      });
    } else {
      // Si no hay countryId (vista global), usar finalize como super admin
      this.formService.finalize(form.id).subscribe({
        next: () => {
          this.snackbarService.open(
            this.translateService.instant('forms.list.messages.published')
          );
          this.loadForms();
        },
        error: (error) => this.snackbarService.notifyHttpError(error),
      });
    }
  }

  private editForm(form: any): void {
    // Fetch full form with schema from API before opening edit dialog
    this.formService.getById(form.id).subscribe({
      next: (fullForm) => {
        this.openEditFormDialog(fullForm);
      },
      error: (err) => this.snackbarService.notifyHttpError(err),
    });
  }

  private openEditFormDialog(form: any): void {
    const dialogRef = this.dialog.open(FormUpsertDialogComponent, {
      width: '90vw',
      maxWidth: '1400px',
      height: '90vh',
      panelClass: ['dialog-fullscreen', 'dialog-safe-area', 'dialog-flex'],
      data: {
        title: this.translateService.instant('forms.form.editTitle'),
        mode: 'edit',
        form: form,
        context: 'country',
        countryId: this.getCurrentCountryId(),
      },
    });

    dialogRef.afterClosed().subscribe((result: FormDialogResult | undefined) => {
      if (result && result.mode === 'edit' && result.formId) {
        this.handleFormUpdate(result.formId, result.payload);
      }
    });
  }

  private deleteForm(form: any): void {
    const dialogRef = this.dialog.open(UiMoleculeDialogDeleteWithConfirmComponent, {
      disableClose: true,
      autoFocus: false,
      panelClass: ['dialog-sm', 'dialog-safe-area'],
      data: { itemName: form.name },
    });

    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (!confirmed) return;

      this.formService.delete(form.id).subscribe({
        next: () => {
          this.loadForms(); // Recargar la lista de forms
        },
        error: (err) => this.snackbarService.notifyHttpError(err),
      });
    });
  }

  private toggleFormAssignment(form: any): void {
    // Toggle the selected state
    form.selected = !form.selected;

    if (form.selected) {
      // TODO: Llamar a API para asignar form a todos los countries
    } else {
      // TODO: Llamar a API para desasignar form de todos los countries
    }

    this.cdr.markForCheck();
  }

  protected onFormPageChange(event: { pageIndex: number; pageSize: number }): void {
    this.formsPageIndex = event.pageIndex;
    this.formsPageSize = event.pageSize;
    this.loadForms();
  }

  protected onFormSortChange(sort: Sort): void {
    const columnToApiField: Record<string, FormListParams['sortBy']> = {
      name: 'name',
      createdAt: 'createdAt',
      updatedAt: 'updatedAt',
      countries: 'publication.assignments',
    };

    if (sort.direction) {
      this.formsSortBy = columnToApiField[sort.active];
      this.formsSortOrder = sort.direction.toUpperCase() as 'ASC' | 'DESC';
    } else {
      this.formsSortBy = undefined;
      this.formsSortOrder = undefined;
    }

    this.formsPageIndex = 0;
    this.loadForms();
  }

  protected onFormsGlobalSearch(searchTerm: string): void {
    this.formsFilter = searchTerm;
    if (searchTerm && searchTerm.length >= 3) {
      this.formsPageIndex = 0;
      this.loadForms();
    } else if (!searchTerm || searchTerm.length === 0) {
      this.formsFilter = undefined;
      this.loadForms();
    }
  }

  protected onFormFilterChange(filters: Record<string, string>): void {
    const filterValue = Object.values(filters).find((v) => v && v.length >= 3);
    if (filterValue) {
      this.formsFilter = filterValue;
      this.formsPageIndex = 0;
      this.loadForms();
    } else if (Object.values(filters).every((v) => !v || v.length === 0)) {
      this.formsFilter = undefined;
      this.loadForms();
    }
  }

  private handleUserUpdate(userId: string, userData: UserUpdate, newPassword?: string): void {
    // Primero actualizar los datos del usuario
    this.userService.update(userId, userData).subscribe({
      next: (updatedUser) => {
        // Si se proporcionó una nueva contraseña, cambiarla por separado
        if (newPassword && newPassword.trim() !== '') {
          this.userService.changePassword(userId, { newPassword }).subscribe({
            next: (userWithNewPassword) => {
              this.loadUsers(); // Recargar tabla al final
            },
            error: (error) => {
              // Aunque falle el cambio de contraseña, los datos del usuario ya se actualizaron
              this.snackbarService.notifyHttpError(error);
              this.loadUsers();
            },
          });
        } else {
          // No se cambió la contraseña, solo recargar la tabla
          this.loadUsers();
        }
      },
      error: (error) => this.snackbarService.notifyHttpError(error),
    });
  }

  private toggleUserStatus(user: any): void {
    const isCurrentlyActive = user.status === 'ACTIVE';
    const action = isCurrentlyActive ? 'block' : 'unblock';
    const actionText = isCurrentlyActive ? 'blocking' : 'unblocking';

    const serviceCall = isCurrentlyActive
      ? this.userService.block(user.id)
      : this.userService.unblock(user.id);

    serviceCall.subscribe({
      next: (updatedUser) => {
        this.loadUsers(); // Recargar la tabla para ver el estado actualizado
      },
      error: (err) => this.snackbarService.notifyHttpError(err),
    });
  }

  protected onExportAllCountries(event: { selectedKeys: string[] }): void {
    this.countryService.getAllForExport({}).subscribe({
      next: (allCountries) => {
        const exportData = allCountries.map((country) => ({
          id: country.id,
          name: country.name,
          activation: country.activation,
          partnersCount: country.counts?.partners || 0,
          beneficiariesCount: country.counts?.beneficiaries || 0,
          createdAt: formatDate(country.createdAt),
          updatedAt: formatDate(country.updatedAt),
        }));

        const countriesTable = this.tables.toArray()[0];
        if (countriesTable) {
          countriesTable.exportFilteredDataToExcel(exportData, event.selectedKeys);
        }
      },
      error: (err) => this.snackbarService.notifyHttpError(err),
    });
  }

  /**
   * Exportar todos los usuarios (no solo los paginados)
   * Nota: En el contexto de Countries, NO se filtra por countryId porque es la vista global de super admin
   */
  protected onExportAllUsers(): void {
    this.userService
      .getAllForExport({
        // Sin filtros - exportar todos los usuarios globalmente
      })
      .subscribe({
        next: (allUsers) => {
          // Mapear al formato de la tabla
          const exportData = allUsers.map((user) => ({
            id: user.id,
            email: user.email,
            firstName: user.firstName,
            lastName: user.lastName,
            enabled: user.status === 'ACTIVE' ? '✓' : '',
            status: user.status,
            createdAt: formatDate(user.createdAt),
            updatedAt: formatDate(user.updatedAt),
          }));

          // Exportar usando la tabla de usuarios (tercera tabla)
          const usersTable = this.tables.toArray()[2];
          if (usersTable) {
            usersTable.exportAllDataToExcel(exportData);
          }
        },
        error: (err) => this.snackbarService.notifyHttpError(err),
      });
  }

  protected onExportAllForms(event: { selectedKeys: string[] }): void {
    this.formService.getAllForExport({}).subscribe({
      next: (allForms) => {
        const exportData = allForms.map((form) => ({
          id: form.id,
          name: form.name,
          published: form.status === 'FINAL' ? '✓' : '',
          createdAt: formatDate(form.createdAt),
          updatedAt: formatDate(form.updatedAt),
          countries: (form as any).publication?.assignments ?? 0,
        }));

        const formsTable = this.tables.toArray()[1];
        if (formsTable) {
          formsTable.exportFilteredDataToExcel(exportData, event.selectedKeys);
        }
      },
      error: (err) => this.snackbarService.notifyHttpError(err),
    });
  }
}
